import React from 'react';
import { BookListBlock } from 'patterns/BookListBlock';
import { ContentList } from 'patterns/ContentList';
import { ExperienceListBlock } from 'patterns/ExperienceListBlock';
import { LogoListBlock } from 'patterns/LogoListBlock';
import { Masthead } from 'patterns/Masthead';
import { Media } from 'patterns/Media';
import { Shell } from 'patterns/Shell';

import '../scss/main.scss';

function AboutPage({
    location: {
        pathname,
    },
}) {
    const data = {
        masthead: {
            title: 'About',
            description: [
                {
                    spans: [],
                    text: 'I’m Rob Simpson, a freelance Jamstack developer and UI designer working with people around the world from my home office in Oxfordshire, UK.',
                    type: 'paragraph',
                },
            ],
            image: {
                alt: '',
                dimensions: {
                    height: 3024,
                    width: 4032,
                },
                url: 'https://images.prismic.io/robsimpson/b654c40f-f586-4b69-8fdb-9ff3018fa444_about-office.jpg?auto=compress,format'
            },
        },
        intro: {
            items :[
                {
                    content_content: {
                        raw: [
                            {spans: [], text: "Behind every project is a business problem that needs solving. I work closely with my clients to discover the problem and help come up with a solution for their business; because the biggest risk is building the wrong thing.", type: "paragraph"},
                        ],
                    },
                    content_title: {
                        text: 'Approach',
                    },
                },
                {
                    content_content: {
                        raw: [
                            {spans: [], text: "My skills span from understanding how users comprehend your web presence to designing beautiful interfaces with these insights in mind and bringing them to life by building performant, maintainable and accessible websites.", type: "paragraph"},
                        ],
                    },
                    content_title: {
                        text: 'Skills',
                    },
                },
            ]
        },
        experience: {
            description: 'A look at the experience I’ve gained over the past 8+ years whilst working at agencies and software companies across the UK.',
            items: [
                {
                    experience_company_name: {
                        text: 'BigTable',
                    },
                    experience_job_title: {
                        text: 'Front-End Developer',
                    },
                    experience_logo: {
                        alt: '',
                        dimensions: {
                            height: 1080,
                            width: 1920,
                        },
                        url: 'https://robsimpson.cdn.prismic.io/robsimpson/fec28063-ccfe-4988-8559-5c4cacf30050_bigtable.svg'
                    },
                    experience_year: {
                        text: '2012 - 2013',
                    },
                },
                {
                    experience_company_name: {
                        text: 'Codeweavers',
                    },
                    experience_job_title: {
                        text: 'Front-End Developer',
                    },
                    experience_logo: {
                        alt: '',
                        dimensions: {
                            height: 1080,
                            width: 1920,
                        },
                        url: 'https://robsimpson.cdn.prismic.io/robsimpson/2baa5308-a6f6-4d61-928a-d05da8eb9f28_codeweavers.svg'
                    },
                    experience_year: {
                        text: '2012 - 2014',
                    },
                },
                {
                    experience_company_name: {
                        text: 'Ridgeway',
                    },
                    experience_job_title: {
                        text: 'UI Developer',
                    },
                    experience_logo: {
                        alt: '',
                        dimensions: {
                            height: 1080,
                            width: 1920,
                        },
                        url: 'https://robsimpson.cdn.prismic.io/robsimpson/a9d1aab5-dbb4-4d7c-be0b-b1ef0dff723c_ridgeway.svg'
                    },
                    experience_year: {
                        text: '2014 - 2016',
                    },
                },
                {
                    experience_company_name: {
                        text: 'Syndicut',
                    },
                    experience_job_title: {
                        text: 'Creative Developer',
                    },
                    experience_logo: {
                        alt: '',
                        dimensions: {
                            height: 1080,
                            width: 1920,
                        },
                        url: 'https://robsimpson.cdn.prismic.io/robsimpson/ebfc3fa8-3521-4191-9c9e-27cdcaea000a_syndicut.svg'
                    },
                    experience_year: {
                        text: '2016 - 2020',
                    },
                },
                {
                    experience_company_name: {
                        text: 'Freelance',
                    },
                    experience_job_title: {
                        text: 'Jamstack Developer',
                    },
                    experience_logo: {
                        alt: '',
                        dimensions: {
                            height: 1080,
                            width: 1920,
                        },
                        url: 'https://robsimpson.cdn.prismic.io/robsimpson/feef2935-20ec-434f-9195-2f8e0f138faf_robsimpson.svg'
                    },
                    experience_year: {
                        text: '2020 - Present',
                    },
                },
            ],
            title: 'My experience',
        },
        clients: {
            background: true,
            description: 'A small selection of clients I’ve helped over the years whilst freelancing and at agencies across Oxfordshire.',
            items: [
                {
                    logo_image: {
                        alt: 'AbAgri',
                        dimensions: {
                            height: 56,
                            width: 96,
                        },
                        url: 'https://robsimpson.cdn.prismic.io/robsimpson/8cc08417-2c51-465b-98c0-fd17b7882f62_Ab+Agri.svg'
                    },
                    logo_size: {
                        text: 'xlarge',
                    },
                },
                {
                    logo_image: {
                        alt: 'Allen Associates',
                        dimensions: {
                            height: 48,
                            width: 172,
                        },
                        url: 'https://robsimpson.cdn.prismic.io/robsimpson/3206b96b-9a3e-4e2d-8758-e24ab327b697_Allen+Associates.svg'
                    },
                    logo_link: {
                        link_type: 'Web',
                        url: 'https://www.allen-associates.co.uk/',
                    },
                    logo_size: {
                        text: 'large',
                    },
                },
                {
                    logo_image: {
                        alt: 'Dashboard',
                        dimensions: {
                            height: 40,
                            width: 210,
                        },
                        url: 'https://robsimpson.cdn.prismic.io/robsimpson/b2382e69-8b8c-4ae1-81f5-44b78709381e_Dashboard.svg'
                    },
                    logo_link: {
                        link_type: 'Web',
                        url: 'https://dashboard.net/',
                    },
                    logo_size: {
                        text: 'medium',
                    },
                },
                {
                    logo_image: {
                        alt: 'HMV',
                        dimensions: {
                            height: 40,
                            width: 94,
                        },
                        url: 'https://robsimpson.cdn.prismic.io/robsimpson/c1a5de4b-0aac-4d37-adb4-dde9122ecbd5_HMV.svg'
                    },
                    logo_link: {
                        link_type: 'Web',
                        url: 'https://store.hmv.com/',
                    },
                    logo_size: {
                        text: 'medium',
                    },
                },
                {
                    logo_image: {
                        alt: 'Money.co.uk',
                        dimensions: {
                            height: 32,
                            width: 192,
                        },
                        url: 'https://robsimpson.cdn.prismic.io/robsimpson/c494eb77-d217-495f-a97d-3d80d1391651_Money.svg'
                    },
                    logo_size: {
                        text: 'small',
                    },
                },
                {
                    logo_image: {
                        alt: 'KintaAI',
                        dimensions: {
                            height: 32,
                            width: 175,
                        },
                        url: 'https://robsimpson.cdn.prismic.io/robsimpson/9001537b-664b-4bcd-9677-575464e4a624_Kinta+Ai.svg'
                    },
                    logo_link: {
                        link_type: 'Web',
                        url: 'https://kinta-ai.com/',
                    },
                    logo_size: {
                        text: 'small',
                    },
                },
                {
                    logo_image: {
                        alt: 'Oxbury',
                        dimensions: {
                            height: 40,
                            width: 222,
                        },
                        url: 'https://robsimpson.cdn.prismic.io/robsimpson/9d70f2f7-d201-425e-be10-3ef9e90acb65_Oxbury.svg'
                    },
                    logo_link: {
                        link_type: 'Web',
                        url: 'https://www.oxbury.com/',
                    },
                    logo_size: {
                        text: 'medium',
                    },
                },
                {
                    logo_image: {
                        alt: 'Oxford University',
                        dimensions: {
                            height: 48,
                            width: 162,
                        },
                        url: 'https://robsimpson.cdn.prismic.io/robsimpson/cb8d2823-be33-4b34-96f1-0646d8c5531e_Oxford+Uni.svg'
                    },
                    logo_link: {
                        link_type: 'Web',
                        url: 'https://www.research.ox.ac.uk/',
                    },
                    logo_size: {
                        text: 'large',
                    },
                },
                {
                    logo_image: {
                        alt: 'Pacific Produce',
                        dimensions: {
                            height: 40,
                            width: 248,
                        },
                        url: 'https://robsimpson.cdn.prismic.io/robsimpson/cb938083-fa93-4486-8149-c4e5846bad1f_Pacific+Produce.svg'
                    },
                    logo_size: {
                        text: 'medium',
                    },
                },
                {
                    logo_image: {
                        alt: 'Prologis',
                        dimensions: {
                            height: 40,
                            width: 217,
                        },
                        url: 'https://robsimpson.cdn.prismic.io/robsimpson/15967a91-1693-481d-85eb-aa957593d11f_Prologis.svg'
                    },
                    logo_size: {
                        text: 'medium',
                    },
                },
                {
                    logo_image: {
                        alt: 'Twinings',
                        dimensions: {
                            height: 40,
                            width: 163,
                        },
                        url: 'https://robsimpson.cdn.prismic.io/robsimpson/d2284dfc-9ba2-4fb5-9990-57c1aa5df3e3_Twinings.svg'
                    },
                    logo_link: {
                        link_type: 'Web',
                        url: 'https://www.twinings.co.uk/',
                    },
                    logo_size: {
                        text: 'medium',
                    },
                },
                {
                    logo_image: {
                        alt: 'Westward Leaning',
                        dimensions: {
                            height: 24,
                            width: 246,
                        },
                        url: 'https://robsimpson.cdn.prismic.io/robsimpson/f80752bb-dbef-4e56-b584-4db4c3fbe3b2_WWL.svg'
                    },
                    logo_size: {
                        text: 'xsmall',
                    },
                },
            ],
            itemsPerRow: '4',
            title: 'Clients I’ve helped',
        },
        gallery: {
            items: [
                {
                    media_caption: {
                        text: 'Getting married to my now wife Monika',
                    },
                    media_image: {
                        alt: '',
                        dimensions: {
                            height: 1476,
                            width: 1180,
                        },
                        url: 'https://images.prismic.io/robsimpson/1151d4fb-311e-4906-b0ef-6c074f33016d_about-wedding.png?auto=compress,format'
                    },
                    media_video_autoplay: false,
                    media_video_upload: {
                        url: '',
                    },
                },
                {
                    media_caption: {
                        text: 'My daughters first time pumpkin picking',
                    },
                    media_image: {
                        alt: '',
                        dimensions: {
                            height: 1476,
                            width: 1180,
                        },
                        url: 'https://images.prismic.io/robsimpson/8be652fb-1a3e-4c6a-8319-215b9aa97b43_about-pumpkin-picking.png?auto=compress,format'
                    },
                },
                {
                    media_caption: {
                        text: 'Visiting the Eden Project with my wife and daughter',
                    },
                    media_image: {
                        alt: '',
                        dimensions: {
                            height: 1476,
                            width: 1180,
                        },
                        url: 'https://images.prismic.io/robsimpson/6857475e-bd7b-4cd5-be93-669b8d95d5c5_about-eden-project.png?auto=compress,format'
                    },
                },
                {
                    media_caption: {
                        text: 'My daughters first visit to the zoo',
                    },
                    media_image: {
                        alt: '',
                        dimensions: {
                            height: 1476,
                            width: 1180,
                        },
                        url: 'https://images.prismic.io/robsimpson/2f4a907b-376a-4174-8762-734975b18bd1_about-zoo.png?auto=compress,format'
                    },
                },
            ],
        },
        projects: {
            description: 'I enjoy creating side projects where I can share content around my interests like cooking, health, business and more.',
            items: [
                {
                    content_media_alignment: 'ltr',
                    content_media_button_color: {
                        text: 'primary',
                    },
                    content_media_button_style: {
                        text: 'outline',
                    },
                    content_media_content: {
                        text: 'Onward is a side project that Kev Simpson and I are working on together. We’re both big fans of cooking healthy recipes and Onward is our place to store those recipes. Our goal is to make the cooking and grocery shopping experience easy and user friendly.',
                    },
                    content_media_image: {
                        alt: '',
                        dimensions: {
                            height: 1080,
                            width: 1920,
                        },
                        url: 'https://images.prismic.io/robsimpson/84bb3fa2-d05d-4fdf-82aa-7bf823e1cd22_test-image.jpg?auto=compress,format'
                    },
                    content_media_title: {
                        text: 'Cooking and sharing healthy recipes through Onward',
                    },
                },
                {
                    content_media_alignment: 'rtl',
                    content_media_button_color: {
                        text: 'primary',
                    },
                    content_media_button_style: {
                        text: 'outline',
                    },
                    content_media_content: {
                        text: 'My personal blog is where I write about various topics I’m interested in from health, money, sustainability and more. I also share some of my favourite products and an honest look at my life as a freelancer.',
                    },
                    content_media_image: {
                        alt: '',
                        dimensions: {
                            height: 1080,
                            width: 1920,
                        },
                        url: 'https://images.prismic.io/robsimpson/84bb3fa2-d05d-4fdf-82aa-7bf823e1cd22_test-image.jpg?auto=compress,format'
                    },
                    content_media_title: {
                        text: 'Writing about health, money, sustainability and more',
                    },
                },
            ],
            title: 'Personal projects',
        },
        books: {
            description: 'A look at some of the books I’ve most enjoyed reading or listening to – ranging from design to business and health.',
            items: [
                {
                    book_category: {
                        text: 'Finance',
                    },
                    book_image: {
                        alt: '',
                        dimensions: {
                            height: 241,
                            width: 160,
                        },
                        url: 'https://images.prismic.io/robsimpson/00b33b14-532e-404c-8cfc-b00c500b14e1_book-iwtytbr.png?auto=compress,format'
                    },
                    book_rating: 5,
                    book_title: {
                        text: 'I will teach you to be rich',
                    },
                    book_url: {
                        link_type: 'Web',
                        url: 'https://www.amazon.co.uk/Will-Teach-You-Rich-2nd/dp/1529306582/',
                    },
                },
                {
                    book_category: {
                        text: 'Health',
                    },
                    book_image: {
                        alt: '',
                        dimensions: {
                            height: 241,
                            width: 160,
                        },
                        url: 'https://images.prismic.io/robsimpson/71ef747c-518c-406c-9d43-464ae2dcef14_book-bls.png?auto=compress,format'
                    },
                    book_rating: 5,
                    book_title: {
                        text: 'Bigger leaner stronger',
                    },
                    book_url: {
                        link_type: 'Web',
                        url: 'https://www.amazon.co.uk/Bigger-Leaner-Stronger-Building-Ultimate/dp/1938895444/',
                    },
                },
                {
                    book_category: {
                        text: 'Productivity',
                    },
                    book_image: {
                        alt: '',
                        dimensions: {
                            height: 241,
                            width: 160,
                        },
                        url: 'https://images.prismic.io/robsimpson/c80007bd-58cf-4504-9c28-5bb8097d3cda_book-hyperfocus.png?auto=compress,format'
                    },
                    book_rating: 5,
                    book_title: {
                        text: 'Hyperfocus',
                    },
                    book_url: {
                        link_type: 'Web',
                        url: 'https://www.amazon.co.uk/Hyperfocus-Work-Less-Achieve-More/dp/1509866132/',
                    },
                },
                {
                    book_category: {
                        text: 'Design',
                    },
                    book_image: {
                        alt: '',
                        dimensions: {
                            height: 241,
                            width: 160,
                        },
                        url: 'https://images.prismic.io/robsimpson/8a0d8a03-45ae-4963-ab4a-59cf3cecce99_book-click.png?auto=compress,format'
                    },
                    book_rating: 5,
                    book_title: {
                        text: 'Click',
                    },
                    book_url: {
                        link_type: 'Web',
                        url: 'https://www.smashingmagazine.com/printed-books/click/',
                    },
                },
                {
                    book_category: {
                        text: 'Development',
                    },
                    book_image: {
                        alt: '',
                        dimensions: {
                            height: 241,
                            width: 160,
                        },
                        url: 'https://images.prismic.io/robsimpson/94717b68-eefe-4690-9788-2c1175c98d9f_book-idp.png?auto=compress,format'
                    },
                    book_rating: 5,
                    book_title: {
                        text: 'Inclusive design patterns',
                    },
                    book_url: {
                        link_type: 'Web',
                        url: 'https://www.smashingmagazine.com/printed-books/inclusive-front-end-design-patterns/',
                    },
                },
                {
                    book_category: {
                        text: 'Psychology',
                    },
                    book_image: {
                        alt: '',
                        dimensions: {
                            height: 241,
                            width: 160,
                        },
                        url: 'https://images.prismic.io/robsimpson/36b145ff-6c7b-43e7-ba1e-44b39f71f414_book-influence.png?auto=compress,format'
                    },
                    book_rating: 5,
                    book_title: {
                        text: 'Influence',
                    },
                    book_url: {
                        link_type: 'Web',
                        url: 'https://www.amazon.co.uk/Influence-Psychology-Robert-Cialdini-PhD/dp/006124189X/',
                    },
                },
            ],
            title: 'My favourite reads',
        },
    };

    return (
        <Shell
            pageDescription="I’m a freelance Jamstack developer and UI designer working with people around the world from my home office in Oxfordshire, UK."
            pageSocialImage=""
            pageTitle="About"
            pathname={pathname}
        >
            <Masthead
                description={data.masthead.description}
                image={data.masthead.image}
                title={data.masthead.title}
            />
            <ContentList
                items={data.intro.items}
            />
            <ExperienceListBlock
                description={data.experience.description}
                items={data.experience.items}
                title={data.experience.title}
            />
            <LogoListBlock
                background={data.clients.background}
                description={data.clients.description}
                items={data.clients.items}
                itemsPerRow={data.clients.itemsPerRow}
                title={data.clients.title}
            />
            <Media
                items={data.gallery.items}
            />
            <BookListBlock
                description={data.books.description}
                items={data.books.items}
                title={data.books.title}
            />
        </Shell>
    );
}

export default AboutPage;
